import { Box } from '@chakra-ui/react';
import { MCustomSelectProps } from '@monetize/types/app';
import React, {
  ChangeEvent,
  FunctionComponent as FC,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { ensureArray } from '../../../api/utils';
import { MCustomSelect } from './MCustomSelect';

export interface MCustomMultiSelectRef {
  updateItem(item: any, newValue: boolean): void;
  updateItems(items: any[]): void;
  clearAll(): void;
}

export interface MCustomMultiSelectProps extends Omit<
  MCustomSelectProps,
  'onChange' | 'value'
> {
  value?: any;
  selectionLimit?: number;
  inputWidth?: string;
  showTagsInInput?: boolean;
  showCheckCount?: boolean;
  showSelectAll?: boolean;
  rightLabel?: boolean;
  onChange?: null | ((val: any[]) => void);
  onRemove?: null | ((val: any, allItems?: any[]) => void);
  onAdd?: null | ((val: any, allItems?: any[]) => void);
}

export const MCustomMultiSelect: FC<MCustomMultiSelectProps> = React.forwardRef<
  any,
  MCustomMultiSelectProps
>(
  (
    {
      value: propValue = [],
      onChange,
      selectionLimit = -1,
      returnItem,
      itemTitle,
      itemValue,
      inputWidth = 'full',
      showTagsInInput,
      showCheckCount,
      onRemove,
      onAdd,
      ...selectProps
    }: MCustomMultiSelectProps,
    ref,
  ) => {
    const [value, setValue] = useState<unknown[]>(() => ensureArray(propValue));

    useEffect(() => {
      setValue(ensureArray(propValue));
    }, [propValue]);

    useImperativeHandle(ref, (): MCustomMultiSelectRef => ({
      updateItem: (item: any, newValue: boolean) => {
        if (newValue) {
          setValue(
            value.filter((existingItem) => existingItem !== item).concat(item),
          );
        } else {
          setValue(value.filter((existingItem) => existingItem !== item));
        }
      },
      updateItems: (items: any[]) => {
        setValue(items);
      },
      clearAll: () => {
        setValue([]);
      },
    }));

    const hasSelectionLimit = selectionLimit >= 0;
    const isAtSelectionLimit =
      hasSelectionLimit && value.length >= selectionLimit;
    const getItemValue = (item: any) =>
      item && typeof item === 'object' && itemValue
        ? item[itemValue as string]
        : item;

    const onCheckedStateChange = (val: any, event: any) => {
      let existingIdx = -1;
      if (returnItem) {
        existingIdx = value.findIndex(
          (item: any) => item[itemValue as string] === val[itemValue as string],
        );
      } else {
        existingIdx = value.findIndex((item: any) => item === val);
      }

      event.target.checked
        ? internalHandleAdd(val, existingIdx)
        : internalHandleRemove(val, existingIdx);
    };

    const internalHandleAdd = (val: any, existingIdx: number) => {
      // Disabled options cover the rendered list, but options loaded from an
      // endpoint aren't in `items`, so enforce the limit here too.
      if (existingIdx < 0 && isAtSelectionLimit) {
        return;
      }
      let newVal = value;
      if (existingIdx < 0) {
        newVal = value.concat(val);
        setValue(newVal);
      }
      onAdd && onAdd(val, newVal);
    };

    const internalHandleRemove = (val: any, existingIdx: number) => {
      const newVal = [...value];
      if (existingIdx >= 0) {
        newVal.splice(existingIdx, 1);
        setValue(newVal);
      }
      onRemove && onRemove(val, newVal);
    };

    const handleOpenStateChange = (isOpen: boolean) => {
      !isOpen && setValue(ensureArray(propValue));
      selectProps.onOpenStateChange && selectProps.onOpenStateChange(isOpen);
    };

    const showContentInInput = !!showTagsInInput;

    // Once the staged selection reaches `selectionLimit`, disable every option
    // that isn't already checked so the user can uncheck but not add more.
    const disabledItems = isAtSelectionLimit
      ? [
          ...(selectProps.disabledItems ?? []),
          ...(selectProps.items ?? [])
            .filter(
              (item) =>
                !value.some(
                  (selected) => getItemValue(selected) === getItemValue(item),
                ),
            )
            .map((item) => (returnItem ? item : getItemValue(item))),
        ]
      : selectProps.disabledItems;

    const handleOnAllSelectEvent = (
      items: Record<string, any>[],
      event: ChangeEvent<HTMLInputElement>,
    ) => {
      if (event.target.checked) {
        const selectedItems = hasSelectionLimit
          ? items.slice(0, selectionLimit)
          : items;
        const values = returnItem
          ? selectedItems
          : selectedItems.map((item) => item[itemValue as string]);
        setValue(values);
        onChange && onChange(values);
      } else {
        setValue([]);
        onChange && onChange([]);
      }
    };

    return (
      <Box w="full" position="relative">
        <MCustomSelect
          ref={ref}
          value={value}
          checkboxDisplay
          showCheckCount={showCheckCount}
          returnItem={returnItem}
          itemTitle={itemTitle}
          itemValue={itemValue}
          onSelectAll={handleOnAllSelectEvent}
          w={inputWidth}
          {...selectProps}
          disabledItems={disabledItems}
          onChangeCheckbox={onCheckedStateChange}
          showContentInInput={showContentInInput}
          onMultiSelectSubmit={() => onChange && onChange(value)}
          onOpenStateChange={handleOpenStateChange}
          clearAllCheckValues={() => setValue([])}
        />
      </Box>
    );
  },
);
